import { useQuery } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { onboardingService } from '../service/onboarding.service';
import type { OnboardingState } from '../model/types';

/** Clé stable : une seule entrée de cache pour `GET /onboarding`. */
export const onboardingKeys = {
  state: ['onboarding', 'state'] as const,
};

/**
 * L'état d'onboarding ne change que sur action explicite de l'utilisateur, et
 * chacune de ces actions renvoie le nouvel état (qu'on réinjecte dans le cache
 * via `syncOnboardingCache`). Une fenêtre de fraîcheur longue suffit donc.
 */
export const ONBOARDING_STALE_TIME = 5 * 60_000;

/**
 * Source unique de `GET /onboarding`.
 *
 * Avant, chaque montage de `OnboardingProvider` déclenchait son propre appel
 * (double montage StrictMode, changement d'identité de `user` après un
 * rafraîchissement de jeton…) : 2 à 3 requêtes par chargement de page. React
 * Query dédoublonne les appels concurrents sur la clé et, passé le premier,
 * ne refait rien tant que la donnée est fraîche.
 */
export function useOnboardingStateQuery(enabled: boolean) {
  return useQuery<OnboardingState>({
    queryKey: onboardingKeys.state,
    queryFn: () => onboardingService.getState(),
    enabled,
    staleTime: ONBOARDING_STALE_TIME,
    gcTime: ONBOARDING_STALE_TIME,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
}

/**
 * Réinjecte dans le cache l'état renvoyé par une mutation d'onboarding
 * (`complete`, `skip`, `reset`, …), pour que le prochain montage ne reparte pas
 * d'une version périmée — et n'ait aucune raison de refaire un appel.
 */
export function syncOnboardingCache(state: OnboardingState): void {
  queryClient.setQueryData(onboardingKeys.state, state);
}
